import {
  ServiceAccountType,
  SyncStatus,
  UtilityAccountStatus,
} from "@tillix/db/enums";
import { computed } from "mobx";
import {
  ExtendedModel,
  isoStringToDateTransform,
  tProp,
  types,
} from "mobx-keystone";
import { compareByDateDesc, findNextDue } from "../../lib/compare-by-date";
import { TModel } from "../../lib/models/t-model";
import { serviceAccountNS } from "./ns.service-account";
import { UtilityServiceAccountModel } from "./utility/model";
import type { InvoiceModel } from "../invoice/model.invoice";
import type { MeterModel } from "../meter/model.meter";
import type { MeterReadingModel } from "../meter/model.meter-reading";
import type { PaymentModel } from "../payment/model.payment";

@serviceAccountNS.model
export class ServiceAccountModel extends ExtendedModel(TModel, {
  type: tProp(types.enum(ServiceAccountType)),
  nameOnAccount: tProp(types.maybeNull(types.string)),
  nickname: tProp(types.maybeNull(types.string)),
  description: tProp(types.maybeNull(types.string)),
  externalId: tProp(types.string),
  balance: tProp(types.number),
  meta: tProp(types.unchecked<Record<string, unknown>>(), () => ({})),
  dueAt: tProp(types.maybeNull(types.dateString)).withTransform(
    isoStringToDateTransform(),
  ),

  // tenant specific fx
  tenantId: tProp(types.string),
  utilityServiceAccount: tProp(
    types.maybeNull(UtilityServiceAccountModel),
    null,
  ).withSetter(),

  syncStatus: tProp(types.enum(SyncStatus), SyncStatus.PENDING).withSetter(),
  syncFailureReason: tProp(types.maybeNull(types.string), null).withSetter(),
}) {
  /**
   * Parsed list of failure reason codes from the most recent sync attempt.
   * The backend stores codes comma-delimited (see `runServiceAccountSync`);
   * this splits them into an array for consumption in the UI, where each
   * code is translated via i18n.
   */
  @computed
  get syncFailureReasons(): string[] {
    if (!this.syncFailureReason) return [];
    return this.syncFailureReason
      .split(",")
      .map((code) => code.trim())
      .filter(Boolean);
  }

  @computed
  get displayName(): string {
    return (
      this.nickname ??
      this.nameOnAccount ??
      this.utilityServiceAccount?.addressRaw ??
      this.externalId
    );
  }

  @computed
  get nameChangeRequestStatus(): "processing" | "success" | "expired" | null {
    if (this.meta?.nameChangeRequest !== "processing") return null;

    const requestedNewName = this.meta?.nameChangeNewName;
    if (typeof requestedNewName !== "string" || requestedNewName === "") {
      return null;
    }

    if (this.nameOnAccount?.toLowerCase() === requestedNewName.toLowerCase()) {
      return "success";
    }

    const requestedAt = this.meta?.nameChangeRequestedAt;
    if (typeof requestedAt === "string") {
      const elapsed = Date.now() - new Date(requestedAt).getTime();
      // 10 days accounts for holiday + weekend clusters (e.g. Christmas → New Year → weekend).
      // Refine once we have data on HC billing turnaround times.
      const tenDaysMs = 10 * 24 * 60 * 60 * 1000;
      if (elapsed > tenDaysMs) return "expired";
    }

    return "processing";
  }

  /** Whether the account is flagged as cash-only in SAP. ACH payments are blocked. */
  @computed
  get isCashOnly(): boolean {
    return !!this.meta?.cashOnly;
  }

  /** Whether the account has a return check flag in SAP. */
  @computed
  get isReturnCheck(): boolean {
    return !!this.meta?.returnCheck;
  }

  /** Whether the account is disconnected per SAP account properties. */
  @computed
  get isAccountDisconnected(): boolean {
    return !!this.meta?.accountDisconnected;
  }

  /** Whether the account has a hardship exemption indicator in SAP. */
  @computed
  get isHardship(): boolean {
    return !!this.meta?.hardshipIndicator;
  }

  /** Whether the account has a disconnection pending in SAP. */
  @computed
  get isDisconnectionPending(): boolean {
    return this.meta?.disconnectionPending != null;
  }

  /** Whether the account is flagged for tax sale in SAP. */
  @computed
  get isTaxSale(): boolean {
    return this.meta?.taxSale != null;
  }

  /**
   * Whether the meter status indicates a warning state (disconnected, pending, or inactive).
   * Suppressed when the account itself is disconnected to avoid duplicate badges
   * (matches the legacy UtilliCX behaviour where account-level disconnect takes priority).
   */
  @computed
  get hasMeterStatusWarning(): boolean {
    if (this.isAccountDisconnected) return false;
    const status = this.utilityServiceAccount?.status;
    return (
      status === UtilityAccountStatus.DISCONNECTED ||
      status === UtilityAccountStatus.DISCONNECT_PENDING ||
      status === UtilityAccountStatus.INACTIVE
    );
  }

  /** i18n key for the meter status when in a warning state. */
  @computed
  get meterStatusLabel(): string | null {
    if (this.isAccountDisconnected) return null;
    switch (this.utilityServiceAccount?.status) {
      case UtilityAccountStatus.DISCONNECTED:
        return "accounts.meterDisconnected";
      case UtilityAccountStatus.DISCONNECT_PENDING:
        return "accounts.meterDisconnectPending";
      case UtilityAccountStatus.INACTIVE:
        return "accounts.meterInactive";
      default:
        return null;
    }
  }

  // TODO: circuit breaker for when utility plugin not enabled for tenant
  @computed
  get addressRaw(): string | undefined {
    return this.utilityServiceAccount?.addressRaw;
  }

  @computed
  get invoices() {
    if (!this.detaching && this.root) {
      return this.root.tilliX.invoices.filter(
        (invoice) => invoice.serviceAccountId === this.id,
      );
    }

    return null;
  }

  @computed
  get unpaidInvoices(): InvoiceModel[] | null {
    return this.invoices?.filter((inv) => inv.amountOpen > 0) ?? null;
  }

  @computed
  get latestInvoice(): InvoiceModel | null {
    const inv = this.invoices;
    if (!inv?.length) {
      return null;
    }
    return inv.toSorted((a, b) =>
      compareByDateDesc(a.invoicedAt, b.invoicedAt),
    )[0];
  }

  @computed
  get nextUnpaidDueAt(): Date | null {
    return findNextDue(this.unpaidInvoices ?? [])?.dueAt ?? null;
  }

  @computed
  get payments(): PaymentModel[] | null {
    if (!this.detaching && this.root) {
      return this.root.tilliX.payments.filter(
        (p) => p.serviceAccountId === this.id,
      );
    }

    return null;
  }

  @computed
  get latestPayment(): PaymentModel | null {
    const pay = this.payments;
    if (!pay?.length) {
      return null;
    }
    return pay.toSorted((a, b) => compareByDateDesc(a.paidAt, b.paidAt))[0];
  }

  @computed
  get meters(): MeterModel[] | null {
    if (!this.detaching && this.root) {
      return this.root.tilliX.meters.filter(
        (meter) => meter.serviceAccountId === this.id,
      );
    }

    return null;
  }

  @computed
  get meterIds(): string[] | null {
    return this.meters?.map((m) => m.id) ?? null;
  }

  @computed
  get meterReadings(): MeterReadingModel[] | null {
    const ms = this.meters;
    if (!this.detaching && this.root && ms) {
      return ms.flatMap((m) => m.readings);
    }

    return null;
  }

  @computed
  get autoPayEnrollments() {
    if (!this.detaching && this.root) {
      return this.root.tilliX.autoPayEnrollments.filter(
        (e) => e.serviceAccountId === this.id,
      );
    }
    return null;
  }
}
